import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import DistributionsDrawer from '../DistributionsDrawer/DistributionsDrawer';
import NoAssociatedCard from '../components/NoAssociatedCard';
import CreateDistributionWizard from '../distributions/create/CreateDistributionWizard';
import { useDeleteDistributionMutation } from '../batchesEndpoints';
import AssociatedDistributionsGrid from '../AssociatedDistributionsGrid';

const ViewAssociatedDistributions = ({
  associatedDistributions,
  availableDate,
  duplicateDistributionId,
  isFetchingShipment,
  isProductShippingManage,
  isShipper,
  isFetchingDistributions,
  onEditPage,
  refreshBatchData,
  setAssociatedDistributions,
  setDistributionEdited,
  selectedDistribution,
  setSelectedDistribution,
  shipmentData,
  totalResults,
  setIsDistributionsVolumeInvalid,
  setUpdatedRows,
  shipmentUpdates,
  updatedRows,
  refetchShipment,
  refetchShipmentDistributions,
}) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });

  const dispatch = useDispatch();
  const [isExplicitDialogOpen, setIsExplicitDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [idToDelete, setIdToDelete] = useState(null);
  const [createDistribution, setCreateDistribution] = useState(false);
  const [deleteDistribution] = useDeleteDistributionMutation();
  const handleCloseDistribution = R.compose(setCreateDistribution, R.F);

  const deleteSelectedDistribution = () => {
    deleteDistribution({ body: { distributionId: idToDelete } })
      .then(res => {
        setIsExplicitDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() =>
        setTimeout(() => {
          setIdToDelete(null);
          setDialogType('');
        }, 500)
      );
  };

  useEffect(() => {
    setDistributionEdited(updatedRows.length > 0);
    setAssociatedDistributions(prev =>
      prev.map(distribution => ({ ...distribution, isEdited: false }))
    );
  }, [updatedRows.length, setAssociatedDistributions, setDistributionEdited]);

  return (
    <Box>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        sx={{ mb: '1rem' }}
      >
        <Typography sx={{ mb: { sm: 0, xs: 2 }, pr: 3 }} variant="h5">
          Associated Distributions
        </Typography>
      </Stack>
      {isFetchingDistributions ? (
        <Box textAlign="center" width="100%">
          <CircularProgress size={48} />
        </Box>
      ) : !associatedDistributions.length ? (
        <Stack sx={{ mb: 9 }}>
          <NoAssociatedCard type="distribution" />
        </Stack>
      ) : (
        <>
          <DistributionsDrawer
            onClose={setSelectedDistribution}
            refreshBatchData={refreshBatchData}
            setSelectedDistribution={setSelectedDistribution}
            {...selectedDistribution}
          />
          <AssociatedDistributionsGrid
            associatedDistributions={associatedDistributions}
            availableDate={availableDate}
            duplicateDistributionId={duplicateDistributionId}
            onEditPage={onEditPage}
            refetchShipment={refetchShipment}
            refetchShipmentDistributions={refetchShipmentDistributions}
            setAssociatedDistributions={setAssociatedDistributions}
            setIsDistributionsVolumeInvalid={setIsDistributionsVolumeInvalid}
            setSelectedDistribution={setSelectedDistribution}
            setUpdatedRows={setUpdatedRows}
            shipment={shipmentData}
            shipmentUpdates={shipmentUpdates}
            totalResults={totalResults}
          />
        </>
      )}
      <DialogExplicitConfirmation
        confirmationMessage={`If you still want to delete the ${dialogType}, enter the word below and click Confirm.`}
        confirmationValue="DELETE"
        dialogOpen={isExplicitDialogOpen}
        handleCloseDialog={() => {
          setIsExplicitDialogOpen(false);
          setTimeout(() => {
            setDialogType('');
            setIdToDelete(null);
          }, 600);
        }}
        label='Type "DELETE"'
        onConfirm={deleteSelectedDistribution}
        title={`Are you sure you want to delete this ${dialogType}?`}
        warningList={['This action cannot be undone.']}
      />
      {R.and(isProductShippingManage, isShipper) && createDistribution && (
        <CreateDistributionWizard
          isFetchingShipment={isFetchingShipment}
          onClose={handleCloseDistribution}
          shipmentData={shipmentData}
        />
      )}
    </Box>
  );
};

ViewAssociatedDistributions.propTypes = {
  associatedDistributions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  availableDate: PropTypes.string.isRequired,
  duplicateDistributionId: PropTypes.string.isRequired,
  isFetchingDistributions: PropTypes.bool.isRequired,
  isFetchingShipment: PropTypes.bool.isRequired,
  isProductShippingManage: PropTypes.bool.isRequired,
  isShipper: PropTypes.bool.isRequired,
  onEditPage: PropTypes.bool.isRequired,
  refetchShipment: PropTypes.func.isRequired,
  refetchShipmentDistributions: PropTypes.func.isRequired,
  refreshBatchData: PropTypes.func.isRequired,
  selectedDistribution: PropTypes.shape({}),
  setAssociatedDistributions: PropTypes.func.isRequired,
  setDistributionEdited: PropTypes.func.isRequired,
  setIsDistributionsVolumeInvalid: PropTypes.func.isRequired,
  setSelectedDistribution: PropTypes.func.isRequired,
  setUpdatedRows: PropTypes.func.isRequired,
  shipmentData: PropTypes.shape({}).isRequired,
  shipmentUpdates: PropTypes.shape({}).isRequired,
  totalResults: PropTypes.number.isRequired,
  updatedRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

ViewAssociatedDistributions.defaultProps = {
  selectedDistribution: null,
};

export default ViewAssociatedDistributions;
